import React, { useState, useEffect } from 'react';
import { fetchVacancies, updateVacancyStatus, updateVacancyConstructionNotes, updateVacancyLeasingNotes } from '../services/vacancyService';

const VacancyStatusGroups = {
  VACANT: "Vacant",
  RENOVATION: "Renovation",
  LEASING: "Leasing",
  PHA: "PHA"
};

const Vacancy = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingLeasingNote, setEditingLeasingNote] = useState(null);
  const [leasingNoteText, setLeasingNoteText] = useState('');
  const [constructionNotesModal, setConstructionNotesModal] = useState(null);
  const [constructionNoteText, setConstructionNoteText] = useState('');

  useEffect(() => {
    loadVacancies();
  }, []);

  const loadVacancies = async () => {
    try {
      setLoading(true);
      const data = await fetchVacancies();
      setVacancies(data);
      setError(null);
    } catch (err) {
      setError('Failed to load vacancies. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateVacancyStatus(id, newStatus);
      // Refresh the list
      loadVacancies();
    } catch (err) {
      setError('Failed to update status. Please try again.');
      console.error(err);
    }
  };

  const startEditingLeasingNote = (vacancy) => {
    setEditingLeasingNote(vacancy.id);
    setLeasingNoteText(vacancy.leasing_notes || '');
  };

  const cancelEditingLeasingNote = () => {
    setEditingLeasingNote(null);
    setLeasingNoteText('');
  };

  const saveLeasingNote = async (id) => {
    try {
      await updateVacancyLeasingNotes(id, leasingNoteText);
      setEditingLeasingNote(null);
      // Refresh the list
      loadVacancies();
    } catch (err) {
      setError('Failed to update leasing notes. Please try again.');
      console.error(err);
    }
  };

  const openConstructionNotesModal = (vacancy) => {
    setConstructionNotesModal(vacancy.id);
    setConstructionNoteText(vacancy.construction_notes || '');
  };

  const closeConstructionNotesModal = () => {
    setConstructionNotesModal(null);
    setConstructionNoteText('');
  };

  const saveConstructionNotes = async () => {
    try {
      await updateVacancyConstructionNotes(constructionNotesModal, constructionNoteText);
      closeConstructionNotesModal();
      // Refresh the list
      loadVacancies();
    } catch (err) {
      setError('Failed to update construction notes. Please try again.');
      console.error(err);
    }
  };

  // Group vacancies by status
  const groupedVacancies = vacancies.reduce((acc, vacancy) => {
    const status = vacancy.status;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(vacancy);
    return acc;
  }, {});

  // Sort status groups in desired order
  const orderedStatuses = [
    VacancyStatusGroups.VACANT,
    VacancyStatusGroups.RENOVATION,
    VacancyStatusGroups.LEASING,
    VacancyStatusGroups.PHA
  ];

  if (loading) {
    return <div className="p-4">Loading vacancies...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        <p>{error}</p>
        <button
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={loadVacancies}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
            
      {vacancies.length === 0 ? (
        <p>No vacant units found.</p>
      ) : (
        <div>
          {orderedStatuses.map(status => {
            const statusVacancies = groupedVacancies[status] || [];
            if (statusVacancies.length === 0) return null;
            
            return (
              <div key={status} className="mb-8">
                <h2 className="text-xl font-semibold mb-3 border-b pb-2">{status} ({statusVacancies.length})</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-4 text-left">Property Address</th>
                        <th className="py-2 px-4 text-left">Owner</th>
                        <th className="py-2 px-4 text-left">Bedrooms</th>
                        <th className="py-2 px-4 text-left">Status</th>
                        <th className="py-2 px-4 text-left">Leasing Notes</th>
                        <th className="py-2 px-4 text-left">Construction Notes</th>
                        <th className="py-2 px-4 text-left">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusVacancies.map(vacancy => (
                        <tr key={vacancy.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-4">{vacancy.full_address}</td>
                          <td className="py-2 px-4">{vacancy.owner}</td>
                          <td className="py-2 px-4">{vacancy.bedrooms}</td>
                          <td className="py-2 px-4">
                            <select
                              value={vacancy.status}
                              onChange={(e) => handleStatusChange(vacancy.id, e.target.value)}
                              className="border rounded py-1 px-2 w-full"
                            >
                              {Object.values(VacancyStatusGroups).map(statusOption => (
                                <option key={statusOption} value={statusOption}>
                                  {statusOption}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-2 px-4">
                            {editingLeasingNote === vacancy.id ? (
                              <div>
                                <textarea
                                  value={leasingNoteText}
                                  onChange={(e) => setLeasingNoteText(e.target.value)}
                                  className="border rounded py-1 px-2 w-full"
                                  rows="3"
                                  placeholder="Leasing/marketing notes..."
                                />
                                <div className="mt-1 flex space-x-2">
                                  <button
                                    onClick={() => saveLeasingNote(vacancy.id)}
                                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditingLeasingNote}
                                    className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="whitespace-pre-wrap">{vacancy.leasing_notes || 'No leasing notes'}</div>
                                <button
                                  onClick={() => startEditingLeasingNote(vacancy)}
                                  className="mt-1 text-sm text-blue-600 hover:underline"
                                >
                                  Edit Leasing Notes
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-4">
                            <button
                              onClick={() => openConstructionNotesModal(vacancy)}
                              className="text-sm text-blue-600 hover:underline"
                            >
                              View Construction Notes
                            </button>
                          </td>
                          <td className="py-2 px-4">
                            {new Date(vacancy.updated_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Construction Notes Modal */}
      {constructionNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Construction Notes</h3>
              <button
                onClick={closeConstructionNotesModal}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>
            <textarea
              value={constructionNoteText}
              onChange={(e) => setConstructionNoteText(e.target.value)}
              className="w-full border rounded p-3 mb-4"
              rows="10"
              placeholder="Construction/renovation notes..."
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={closeConstructionNotesModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={saveConstructionNotes}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vacancy;